// src/components/CompanyManagementTab.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  IconButton,
  Tooltip,
  Tabs,
  Tab
} from '@mui/material';
import {
  Building2,
  Users,
  FileText,
  Edit3,
  CreditCard,
  ChevronRight,
  Check,
  Download,
  Globe,
  Mail,
  Phone,
  User,
  Calendar,
  Plus,
  Trash2
} from 'lucide-react';
import adminService from './bulkopsadmin/AdminService';
import CompanyManager from './CompanyManager';

// TabPanel Component
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`company-tabpanel-${index}`}
      aria-labelledby={`company-tab-${index}`}
      {...other}
      sx={{
        display: value !== index ? 'none' : 'block',
        p: 3
      }}
    >
      {value === index && children}
    </Box>
  );
};

const CompanyManagementTab = () => {
  // State for company data
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [companyInvoices, setCompanyInvoices] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyDetailTab, setCompanyDetailTab] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  // true while the editor is open to create a new company (no company passed in)
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch companies when the component mounts
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Fetch company details, users, and invoices when a company is selected
  useEffect(() => {
    if (selectedCompany) {
      fetchCompanyDetails();
      fetchCompanyUsers();
      fetchCompanyInvoices();
    }
  }, [selectedCompany]);

  // Fetch companies for the current user
  const fetchCompanies = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getUserCompanies();
      setCompanies(data || []);
      
      // If this is an admin with only one company, auto-select it
      if (data && data.length === 1) {
        setSelectedCompany(data[0]);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to fetch companies: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch detailed information for the selected company
  const fetchCompanyDetails = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getCompanyDetails(selectedCompany.id);
      // Update the selected company with the detailed information
      setSelectedCompany(data);
    } catch (err) {
      console.error('Error fetching company details:', err);
      setError('Failed to fetch company details: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch users for the selected company
  const fetchCompanyUsers = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getCompanyUsers(selectedCompany.id);
      setCompanyUsers(data || []);
    } catch (err) {
      console.error('Error fetching company users:', err);
      setError('Failed to fetch company users: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch invoices for the selected company
  const fetchCompanyInvoices = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    setError('');

    try {
      const data = await adminService.getCompanyInvoices(selectedCompany.id);
      setCompanyInvoices(data || []);
    } catch (err) {
      console.error('Error fetching company invoices:', err);
      setError('Failed to fetch company invoices: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Open company edit dialog
  const handleOpenEditDialog = () => {
    setEditDialogOpen(true);
  };

  // Close company edit dialog
  // Delete the selected company; its users are unlinked by the backend, not deleted
  const handleDeleteCompany = async () => {
    setDeleting(true);
    try {
      await adminService.deleteCompany(selectedCompany.id);
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      fetchCompanies();
    } catch (err) {
      setError('Failed to delete company: ' + (err.response?.data?.message || err.message));
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreatingCompany(true);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = (refreshData = false) => {
    setEditDialogOpen(false);
    if (creatingCompany) {
      // The editor closes itself after saving without a flag, so always reload the list
      setCreatingCompany(false);
      fetchCompanies();
      return;
    }
    // The editor calls onClose() without a flag after saving, so always reload what it may have changed
    fetchCompanyDetails();
    fetchCompanyUsers();
    fetchCompanyInvoices();
    fetchCompanies();
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (err) {
      return 'Invalid date';
    }
  };

  // Format currency
  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setCompanyDetailTab(newValue);
  };

  // Tab IDs
  const a11yProps = (index) => ({
    id: `company-tab-${index}`,
    'aria-controls': `company-tabpanel-${index}`,
  });

  // Render company list when no company is selected
  const renderCompanyList = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" component="h2">
            Company Management
          </Typography>
          <Button variant="contained" startIcon={<Plus size={18} />} onClick={handleOpenCreateDialog}>
            Add Company
          </Button>
        </Box>

        {loading && !companies.length ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {companies.map(company => (
              <Grid item xs={12} md={6} lg={4} key={company.id}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 3
                    }
                  }}
                  onClick={() => setSelectedCompany(company)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Building2 size={24} style={{ marginRight: 12, opacity: 0.7 }} />
                        <Typography variant="h6">{company.name}</Typography>
                      </Box>
                      <Chip 
                        label={company.status}
                        size="small"
                        color={company.status === 'active' ? 'success' : company.status === 'trial' ? 'warning' : 'default'}
                      />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Globe size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                      <Typography variant="body2" color="text.secondary">
                        {company.domain || 'No domain set'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Mail size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                      <Typography variant="body2" color="text.secondary">
                        {company.email || 'No email set'}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Chip 
                          label={company.planType}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Users size={16} style={{ marginRight: 4 }} />
                        <Typography variant="body2">
                          {company.user_count || 0} Users
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button 
                        endIcon={<ChevronRight size={16} />}
                        size="small"
                      >
                        View Details
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    );
  };

  // Render company details when a company is selected
  const renderCompanyDetails = () => {
    if (!selectedCompany) return null;

    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              variant="outlined"
              size="small"
              onClick={() => setSelectedCompany(null)}
              sx={{ mr: 2 }}
            >
              Back to Companies
            </Button>
            <Typography variant="h6">
              {selectedCompany.name}
            </Typography>
            <Chip 
              label={selectedCompany.status}
              size="small"
              color={selectedCompany.status === 'active' ? 'success' : selectedCompany.status === 'trial' ? 'warning' : 'default'}
              sx={{ ml: 2 }}
            />
            <Chip 
              label={selectedCompany.planType}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Trash2 size={16} />}
              onClick={() => setDeleteDialogOpen(true)}
            >
              Delete Company
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Edit3 size={16} />}
              onClick={handleOpenEditDialog}
            >
              Edit Company
            </Button>
          </Box>
        </Box>

        <Paper sx={{ borderRadius: 1, overflow: 'hidden', mb: 3 }}>
          <Tabs
            value={companyDetailTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f9fafb' }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Building2 size={16} style={{ marginRight: 8 }} />
                  <span>Overview</span>
                </Box>
              } 
              {...a11yProps(0)} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Users size={16} style={{ marginRight: 8 }} />
                  <span>Users ({companyUsers.length})</span>
                </Box>
              } 
              {...a11yProps(1)} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <FileText size={16} style={{ marginRight: 8 }} />
                  <span>Invoices ({companyInvoices.length})</span>
                </Box>
              } 
              {...a11yProps(2)} 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CreditCard size={16} style={{ marginRight: 8 }} />
                  <span>Billing</span>
                </Box>
              } 
              {...a11yProps(3)} 
            />
          </Tabs>
          
          {/* Overview Tab */}
          <TabPanel value={companyDetailTab} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Company Information
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Building2 size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                          <Typography variant="body2" color="text.primary">
                            {selectedCompany.name}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Globe size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                          <Typography variant="body2" color="text.secondary">
                            {selectedCompany.domain || 'No domain set'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Mail size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                          <Typography variant="body2" color="text.secondary">
                            {selectedCompany.email || 'No email set'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Phone size={16} style={{ marginRight: 8, opacity: 0.7 }} />
                          <Typography variant="body2" color="text.secondary">
                            {selectedCompany.phone || 'No phone set'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Address:</strong> {selectedCompany.address || 'No address set'}
                          {selectedCompany.city && `, ${selectedCompany.city}`}
                          {selectedCompany.state && `, ${selectedCompany.state}`}
                          {selectedCompany.zip && ` ${selectedCompany.zip}`}
                          {selectedCompany.country && `, ${selectedCompany.country}`}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Subscription Details
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Plan:</Typography>
                      <Typography variant="body2" color="text.primary">
                        <Chip 
                          label={selectedCompany.planType}
                          size="small"
                          color="primary"
                        />
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Status:</Typography>
                      <Typography variant="body2" color="text.primary">
                        <Chip 
                          label={selectedCompany.status}
                          size="small"
                          color={selectedCompany.status === 'active' ? 'success' : selectedCompany.status === 'trial' ? 'warning' : 'default'}
                        />
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Billing Cycle:</Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedCompany.billingCycle === 'monthly' ? 'Monthly' : 
                         selectedCompany.billingCycle === 'quarterly' ? 'Quarterly' : 
                         selectedCompany.billingCycle === 'annually' ? 'Annually' : 
                         selectedCompany.billingCycle || 'N/A'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Maximum Users:</Typography>
                      <Typography variant="body2" color="text.primary">
                        {selectedCompany.maxUsers || 'Unlimited'}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Features
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Check size={16} color={selectedCompany.features?.enable_ai_signature ? "green" : "gray"} style={{ marginRight: 8 }} />
                      <Typography variant="body2" color={selectedCompany.features?.enable_ai_signature ? "text.primary" : "text.disabled"}>
                        AI-Powered Signatures
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Check size={16} color={selectedCompany.features?.enable_signature_templates ? "green" : "gray"} style={{ marginRight: 8 }} />
                      <Typography variant="body2" color={selectedCompany.features?.enable_signature_templates ? "text.primary" : "text.disabled"}>
                        Signature Templates
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Users Tab */}
          <TabPanel value={companyDetailTab} index={1}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<User size={16} />}
                onClick={handleOpenEditDialog}
              >
                Manage Users
              </Button>
            </Box>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Login</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyUsers.length > 0 ? (
                    companyUsers.map((user) => (
                      <TableRow key={user.id} hover>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.company_role}
                            color={
                              user.company_role === 'superadmin' ? 'error' : 
                              user.company_role === 'admin' ? 'secondary' : 
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={user.status}
                            color={user.status === 'active' ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Box sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No users found for this company
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          
          {/* Invoices Tab */}
          <TabPanel value={companyDetailTab} index={2}>
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyInvoices.length > 0 ? (
                    companyInvoices.map((invoice) => (
                      <TableRow key={invoice.id} hover>
                        <TableCell>{invoice.invoice_number || invoice.id}</TableCell>
                        <TableCell>{formatDate(invoice.date || invoice.issue_date)}</TableCell>
                        <TableCell>{invoice.description}</TableCell>
                        <TableCell>{formatCurrency(invoice.amount, invoice.currency)}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={invoice.status}
                            color={
                              invoice.status === 'paid' ? 'success' : 
                              invoice.status === 'pending' ? 'warning' : 
                              invoice.status === 'overdue' ? 'error' : 
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Download Invoice">
                            <IconButton size="small">
                              <Download size={16} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Box sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No invoices found for this company
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          
          {/* Billing Tab */}
          <TabPanel value={companyDetailTab} index={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Billing Information
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                            Company Name:
                          </Typography>
                          <Typography variant="body2">
                            {selectedCompany.billing?.companyName || selectedCompany.name}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                            Contact Name:
                          </Typography>
                          <Typography variant="body2">
                            {selectedCompany.billing?.contactName || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                            Contact Email:
                          </Typography>
                          <Typography variant="body2">
                            {selectedCompany.billing?.contactEmail || selectedCompany.email || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                            VAT/Tax ID:
                          </Typography>
                          <Typography variant="body2">
                            {selectedCompany.billing?.vatId || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                            Billing Address:
                          </Typography>
                          <Typography variant="body2">
                            {selectedCompany.billing?.billingAddress || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ width: 140 }}>
                            Payment Method:
                          </Typography>
                          <Typography variant="body2">
                            {selectedCompany.billing?.paymentMethod === 'invoice' ? 'Invoice' :
                             selectedCompany.billing?.paymentMethod === 'credit_card' ? 'Credit Card' :
                             selectedCompany.billing?.paymentMethod === 'bank_transfer' ? 'Bank Transfer' :
                             selectedCompany.billing?.paymentMethod || 'Not specified'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Subscription Summary
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Current Plan:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {selectedCompany.planType} ({selectedCompany.billingCycle})
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Users:</Typography>
                      <Typography variant="body2">
                        {companyUsers.length} / {selectedCompany.maxUsers || 'Unlimited'}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Next Billing Date:</Typography>
                      <Typography variant="body2">
                        {selectedCompany.nextBillingDate ? formatDate(selectedCompany.nextBillingDate) : 'Not available'}
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<CreditCard size={16} />}
                        onClick={handleOpenEditDialog}
                      >
                        Update Billing
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>
      </Box>
    );
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {selectedCompany ? renderCompanyDetails() : renderCompanyList()}
      
      {/* Company Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => handleCloseEditDialog()}
        maxWidth="md"
        fullWidth
      >
        {(selectedCompany || creatingCompany) && (
          <CompanyManager
            open={editDialogOpen}
            onClose={(refreshData) => handleCloseEditDialog(refreshData)}
            company={creatingCompany ? null : selectedCompany}
          />
        )}
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete {selectedCompany?.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently deletes the company. Its {companyUsers.length} user(s) keep their accounts
            but are removed from the company. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteCompany} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CompanyManagementTab;